import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { requestEmailVerification, syncEmailVerificationStatus } from '../api/authVerification'
import { useUser } from '../contexts/UserContext'
import { auth } from '../config/firebase'
import { firebaseAuthService } from '../services/firebaseAuthService'

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

function verificationSentMessage(email?: string | null) {
  const address = email || 'your email'
  return `Link sent to ${address}. Check inbox and Spam, then open the newest email.`
}

const VerifyEmailPage = () => {
  const navigate = useNavigate()
  const query = useQuery()
  const oobCode = query.get('oobCode')
  const mode = query.get('mode')
  const token = query.get('token')
  const { user, refreshUser, logout } = useUser()

  const [status, setStatus] = useState<'idle' | 'checking' | 'confirming' | 'confirmed' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isApplyingLink, setIsApplyingLink] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const hasVerificationLink = Boolean(oobCode && (mode === 'verifyEmail' || !mode))

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  useEffect(() => {
    const verificationSent = sessionStorage.getItem('verificationEmailSent')
    const justRegistered = sessionStorage.getItem('justRegistered')
    if (verificationSent) {
      sessionStorage.removeItem('justRegistered')
      setResendCooldown(60)
      setMessage(verificationSentMessage(user?.email))
    } else if (justRegistered) {
      sessionStorage.removeItem('justRegistered')
      setMessage(
        `Account created for ${user?.email || 'your email'}. Send a verification email below.`
      )
    }
  }, [user?.email])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const checkVerificationStatus = async () => {
      const currentUser = auth.currentUser
      if (!currentUser || hasVerificationLink) return
      try {
        await currentUser.reload()
        if (currentUser.emailVerified) {
          await syncEmailVerificationStatus()
          await refreshUser()
          setStatus('confirmed')
          setMessage('Email is already verified. Redirecting...')
          timeoutId = setTimeout(() => navigate('/dashboard', { replace: true }), 2000)
        }
      } catch (error) {
        console.error('Error checking verification status:', error)
      }
    }
    checkVerificationStatus()
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [refreshUser, navigate, hasVerificationLink])

  // Apply oobCode from the email link (custom Firebase action handler → /verify-email?mode=verifyEmail&oobCode=...)
  useEffect(() => {
    if (!oobCode || (mode && mode !== 'verifyEmail')) return

    let cancelled = false
    ;(async () => {
      setStatus('confirming')
      setIsApplyingLink(true)
      setMessage('Confirming your email…')
      try {
        const result = await firebaseAuthService.applyEmailVerificationCode(oobCode)
        if (cancelled) return
        if (!result.success) {
          setStatus('error')
          setMessage(result.error || 'Could not verify email. Request a new link below.')
          return
        }
        await refreshUser()
        setStatus('confirmed')
        setMessage('Email verified successfully! Redirecting…')
        navigate('/verify-email', { replace: true })
      } catch (e: any) {
        if (cancelled) return
        setStatus('error')
        setMessage(e?.message || 'Failed to verify email. The link may have expired.')
      } finally {
        if (!cancelled) setIsApplyingLink(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [oobCode, mode, refreshUser, navigate])

  // Legacy token param (no-op sync path)
  useEffect(() => {
    if (!token || oobCode) return
    ;(async () => {
      try {
        setStatus('checking')
        const currentUser = auth.currentUser
        if (!currentUser) {
          setStatus('error')
          setMessage('Please sign in to verify your email.')
          return
        }
        await currentUser.reload()
        if (currentUser.emailVerified) {
          await syncEmailVerificationStatus()
          await refreshUser()
          setStatus('confirmed')
          setMessage('Email verified successfully! Redirecting...')
        } else {
          setStatus('idle')
          setMessage('Please use the button below to send a new verification email.')
        }
      } catch (e: any) {
        setStatus('error')
        setMessage(e?.message || 'Failed to verify email. The link may have expired.')
      }
    })()
  }, [token, oobCode, refreshUser])

  useEffect(() => {
    if (user && user.emailVerified && status === 'confirmed') {
      const timer = setTimeout(() => {
        if (!user.onboardingCompleted) {
          if (user.userType === 'FOOD_BANK') {
            navigate('/food-bank-onboarding', { replace: true })
          } else if (user.userType === 'SUPPLIER') {
            navigate('/supplier-onboarding', { replace: true })
          } else if (user.userType === 'VOLUNTEER') {
            navigate('/onboarding', { replace: true })
          } else {
            navigate('/dashboard', { replace: true })
          }
        } else {
          navigate('/dashboard', { replace: true })
        }
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [user, status, navigate])

  useEffect(() => {
    if (user?.userType === 'ADMIN') {
      navigate('/dashboard', { replace: true })
    }
  }, [user, navigate])

  const sendLink = async () => {
    if (user?.userType === 'ADMIN') {
      navigate('/dashboard', { replace: true })
      return
    }
    try {
      if (isSending || resendCooldown > 0) return
      setIsSending(true)
      setMessage(null)
      setStatus('idle')
      await requestEmailVerification()
      sessionStorage.setItem('verificationEmailSent', 'true')
      setResendCooldown(60)
      setMessage(verificationSentMessage(user?.email))
    } catch (e: any) {
      setStatus('error')
      const errorMessage = String(e?.message || '')
      if (errorMessage.toLowerCase().includes('too-many-requests') || errorMessage.toLowerCase().includes('too many')) {
        setResendCooldown(120)
        setMessage('Too many requests. Wait a few minutes, then try again. Check Spam if needed.')
      } else {
        setMessage(errorMessage || 'Failed to send verification email.')
      }
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <h1 className="text-xl font-bold text-gray-900">Verify Email</h1>
        <p className="text-sm text-gray-600 mt-1">
          {user?.email ? `Signed in as ${user.email}.` : 'Sign in to request a verification email.'}
        </p>

        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          {message ||
            'If you just created an account, check your inbox and Spam for the verification email. Need a new link? Use the button below — logging in does not send another email.'}
        </div>

        {isApplyingLink && (
          <p className="text-sm text-gray-600 mt-4">Applying verification link…</p>
        )}
        {status === 'confirmed' && (
          <p className="text-sm text-green-700 mt-4">Email verified successfully!</p>
        )}
        {status === 'error' && hasVerificationLink && (
          <p className="text-sm text-red-700 mt-4">
            That link expired. Send a new one below.
          </p>
        )}

        <div className="mt-6 flex flex-col gap-3">
          <button
            onClick={sendLink}
            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isSending || resendCooldown > 0 || isApplyingLink}
          >
            {isSending
              ? 'Sending...'
              : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Send verification email'}
          </button>
          <button
            type="button"
            onClick={async () => {
              await logout()
              navigate('/login', { replace: true })
            }}
            className="btn-outline"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailPage
